/**
 * Plan feature definitions — source of truth for all plan-gated behavior.
 *
 * 2026-06-10 rewrite:
 *   - starter (customer-facing "Basic"): 50 monthly credits, 5 campaigns max,
 *     200 leads max in DB, 30 leads max per campaign, 5 content posts/week,
 *     free Ollama-hosted gemma3:12b as the LLM (zero outbound cost).
 *   - pro: 100 monthly credits, 10 campaigns max, 400 leads max in DB,
 *     60 leads max per campaign, 10 content posts/week, claude-sonnet.
 *   - agency: hidden legacy tier (no signup path; pricing UI hides it). Kept
 *     in PLAN_FEATURES so existing rows route correctly.
 *
 * 2026-06-15: added maxLeadsPerCampaign (Basic 30 / Pro 60) — a per-campaign
 * cap distinct from maxLeadsInDb (account-wide) and maxCampaigns (count of
 * campaigns). Enforced server-side at every path that points a lead's
 * campaign_id at a campaign; see utils/campaign-cap.ts.
 */

import type { Plan, PlanFeatures, PlanInfo } from '../types/plans'

export const PLAN_FEATURES: Record<Plan, PlanFeatures> = {
  starter: {
    maxLeadsInDb: 200,
    maxLeadsPerCampaign: 30,
    maxCampaigns: 5,
    maxLinkedInAccounts: 1,
    followUpSteps: 3,
    dailyConnectionLimit: 25,
    dailyMessageLimit: 50,
    contentPostsPerWeek: 5,
    // 2026-07-27 owner spec: Basic gets 1 automated post/week; the rest of
    // the weekly allowance is manual/scheduled posting.
    autoPostsPerWeek: 1,
    aiOutreach: true,
    aiReplyHandling: 'basic',
    contentEngine: true,
    engagementTracking: false,
    engagementToDm: false,
    knowledgeBase: false,
    intentSignals: 'basic',
    leadScoring: 'icp_only',
    competitorMonitoring: false,
    calendlyIntegration: false,
    // 2026-06-10: Starter LLM moved to Ollama Cloud (free hosted instruct
    // model). Zero per-call cost vs ~$0.12/day claude-haiku, so the free
    // tier no longer eats into the AI budget at all. 2026-07-16: gemma4:31b
    // (Ollama retired gemma3:12b on 2026-07-15).
    aiModelTier: 'gemma4:31b',
    teamSupport: false,
    prioritySupport: false,
    adminDashboard: false,
    maxKbStorageMb: 0,
    // 2026-07-22: 50 → 100 per owner directive (Basic 100 / Pro 200).
    monthlyCredits: 100,
    autoDiscoveryCredits: 50,
  },
  pro: {
    maxLeadsInDb: 400,
    maxLeadsPerCampaign: 60,
    maxCampaigns: 10,
    // 2026-07-23: 3 → 1. The connect flow is single-account (connecting a new
    // LinkedIn account replaces the previous one), so advertising 3 on the
    // billing page was selling something that does not exist. Restore when
    // multi-account/seats actually ships.
    maxLinkedInAccounts: 1,
    followUpSteps: 5,
    dailyConnectionLimit: 40,
    dailyMessageLimit: 80,
    contentPostsPerWeek: 10,
    // 2026-07-27 owner spec: Pro gets 1 automated post per day.
    autoPostsPerWeek: 7,
    aiOutreach: true,
    aiReplyHandling: 'full_autonomous',
    contentEngine: true,
    engagementTracking: true,
    engagementToDm: true,
    knowledgeBase: true,
    intentSignals: 'full',
    leadScoring: 'icp_intent',
    competitorMonitoring: true,
    calendlyIntegration: true,
    aiModelTier: 'claude-sonnet',
    teamSupport: false,
    prioritySupport: false,
    adminDashboard: false,
    maxKbStorageMb: 50,
    // 2026-07-22: 100 → 200 per owner directive (Basic 100 / Pro 200).
    monthlyCredits: 200,
    autoDiscoveryCredits: 200,
  },
  agency: {
    maxLeadsInDb: Infinity,
    maxLeadsPerCampaign: Infinity,
    maxCampaigns: Infinity,
    maxLinkedInAccounts: 10,
    followUpSteps: 7,
    dailyConnectionLimit: 50,
    dailyMessageLimit: 100,
    // 2026-07-27: was 5, which left the hidden legacy tier below Pro's 10.
    // Comp accounts resolve limits through agency, so keep it >= Pro.
    contentPostsPerWeek: 10,
    autoPostsPerWeek: 7,
    aiOutreach: true,
    aiReplyHandling: 'full_autonomous',
    contentEngine: true,
    engagementTracking: true,
    engagementToDm: true,
    knowledgeBase: true,
    intentSignals: 'full',
    leadScoring: 'icp_intent',
    competitorMonitoring: true,
    calendlyIntegration: true,
    aiModelTier: 'claude-opus',
    teamSupport: true,
    prioritySupport: true,
    adminDashboard: true,
    maxKbStorageMb: 200,
    monthlyCredits: 2000,
    autoDiscoveryCredits: 500,
  },
} as const

// 2026-07-22: $39/$149 → $29/$59 with the move to the new Stripe account.
export const PLAN_PRICING: Record<Plan, number> = {
  starter: 29,
  pro: 59,
  agency: 399,
} as const

// 2026-06-07: Agency removed from the customer-facing plan list. The tier
// definitions live in PLAN_FEATURES.agency for legacy migrated rows + comp
// account routing, but no new customer can sign up for it. Pricing tables,
// PlanGate ordering, and the dashboard plan badge all derive from this
// PLANS array — dropping agency here hides it from every UI surface in
// one move. Backend code that still needs the agency definitions reads
// PLAN_FEATURES.agency directly.
export const PLANS: readonly PlanInfo[] = [
  // 2026-07-23: synced to the live Stripe amounts ($29/$59 beta pricing).
  { id: 'starter', name: 'Basic', priceMonthly: 29, features: PLAN_FEATURES.starter },
  { id: 'pro', name: 'Pro', priceMonthly: 59, features: PLAN_FEATURES.pro },
] as const

/** Only the two customer-facing plans. Comp accounts route to 'pro' via
 *  resolveEffectivePlan; agency stays in PLAN_FEATURES for backend data. */
export const ALL_PLANS: readonly Plan[] = ['starter', 'pro'] as const
